<template>
  <div class="comment__file">
    <Spinner
      v-if="loading"
      :loading="loading"
    />
    <VideoAttachmentItem
      v-else-if="isVideo"
      :key="currentVideoUrl"
      :file="item"
      :videoPath="currentVideoUrl"
      :hideInfo="true"
      :hidePoster="true"
      fancyboxData="fancybox_comment"
      :useSharedFancybox="true"
      class="comment__file-video"
      @click="updateVideoLink"
    />
    <ImageItem
      v-else-if="isImage"
      :attachment="item"
      :blobUrl="blobUrl"
      fancyboxData="fancybox_comment"
      :fileMetadataId="item.id"
      :loadFullSize="true"
      class="comment__file-image"
    />
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component"
import { Prop, Watch } from "vue-property-decorator"
import { Action, Getter } from "s-vuex-class"

import ImageItem from "@/components/File/ImageItem.vue"
import Spinner from "@/components/Spinner/index.vue"
import VideoAttachmentItem from "@/components/VideoPlayer/VideoAttachmentItem.vue"

import { isImage } from "@/helpers/isImg"
import { isVideo } from "@/helpers/isVideo"
import { FileMetaData } from "@/models"
import { Actions, Getters } from "@/store"

@Options({
  name: "FileCommentItem",
  components: { ImageItem, VideoAttachmentItem, Spinner }
})
export default class FileCommentItem extends Vue {
  @Prop({
    required: true
  })
  item: FileMetaData
  @Prop({ default: true }) readonly projectScoped: boolean

  @Getter(Getters.GET_CURRENT_PROJECT_ID) readonly projectId: string

  @Action(Actions.ATTACHMENT_VIEW) loadFile: ({
    fileMetadataId,
    fileName
  }: {
    fileMetadataId: string
    fileName?: string
  }) => Promise<Blob>
  @Action(Actions.ATTACHMENT_VIDEO_URL) videoUrl: ({
    projectId,
    attachmentId,
    projectScoped
  }: {
    projectId: string
    attachmentId: string
    projectScoped?: boolean
  }) => Promise<string>

  blobUrl = ""
  img = ""
  loading = true
  currentVideoUrl = ""

  get isRemoved() {
    return this.item.deleted
  }

  get fileType() {
    return isImage(this.item.name) ? "image" : "video"
  }

  get isVideo() {
    return isVideo(this.item.name)
  }

  get isImage() {
    return isImage(this.item.name)
  }

  async mounted() {
    if (this.item.custom && this.item.pendingFile) {
      if (this.isImage) {
        this.blobUrl = URL.createObjectURL(this.item.pendingFile)
      }
      if (this.isVideo) {
        this.currentVideoUrl = URL.createObjectURL(this.item.pendingFile)
      }
      this.loading = false
      return
    }
    if (this.isRemoved || this.item.custom) {
      this.loading = false
      return
    }
    if (this.isVideo) {
      this.updateVideoLink()
      this.loading = false
    }
    if (this.isImage) {
      this.$wait.start("get.attachment")

      const blob: Blob = await this.loadFile({
        fileMetadataId: this.item.id
      })

      if (!blob) {
        this.$wait.end("get.attachment")
        return
      }

      this.blobUrl = URL.createObjectURL(blob)

      if (!this.blobUrl) {
        this.$wait.end("get.attachment")
        return
      }

      this.$wait.end("get.attachment")
    }
  }

  async updateVideoLink() {
    if (this.item.custom) {
      return
    }

    this.currentVideoUrl = await this.videoUrl({
      projectId: this.projectId,
      attachmentId: this.item.id,
      projectScoped: this.projectScoped
    })
  }

  @Watch("blobUrl")
  onChange() {
    this.loading = false
  }
}
</script>
